import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';

/**
 * LIVEMOA STEP 2-P7-E · Common Warehouse Scan Input.
 *
 * Shared component for pick / inspect / pack scan flows in the Operator
 * Dashboard AND (from BARCODE/WMS A3) the Seller Fulfillment ScanModal.
 * NEVER hits a global barcode search API — matching is scoped to the
 * current order via the injected `scanUrl` endpoint.  Default endpoint
 * is the operator READ-ONLY scan-match (unchanged from 2-P7-E).
 *
 * Features
 * --------
 *  - Keyboard-wedge USB / BT scanner (Enter submit · autofocus · trim).
 *  - Optional camera scanner via browser BarcodeDetector when available;
 *    graceful fallback message when unsupported (no forced permission).
 *  - Manual quantity fallback for barcode-less items.
 *  - Overscan / wrong-item / ambiguous-barcode UX (Korean).
 *  - Frontend session state only for operator mode — no DB writes per
 *    scan.  Seller mode delegates persistence to the injected server
 *    endpoint (see `scanUrl`).
 *
 * Props
 * -----
 *  - sid           seller_order_id (required · substituted into default URL)
 *  - progress      [{product_id, variant_key, product_name, required_qty}]
 *                  (operator mode only; ignored when `onScanResult` provided)
 *  - scanned       {`${product_id}::${variant_key}`: number}
 *                  (operator mode only)
 *  - onScanned     (product_id, variant_key, matched_by) → void
 *                  (operator mode only)
 *  - onReset       () → void
 *  - disabled      boolean
 *  - scanUrl       NEW · A3 · endpoint path (default
 *                  `/operator/orders/${sid}/scan-match`).  Backend contract
 *                  MUST accept `{code}` and return either
 *                  a) operator READ-only shape (matched/reason/detail…) OR
 *                  b) seller WRITE shape (fulfillment/progress_entry/all_done…)
 *                  · component only shapes UI · parent is source of truth.
 *  - onScanResult  NEW · A3 · (rawResponse|{error}) → void.  When provided,
 *                  the component STOPS its internal operator-style progress
 *                  logic and bubbles the untouched server response to the
 *                  parent.  Existing operator sites do NOT pass this prop,
 *                  so their behaviour is byte-identical to pre-A3.
 *  - onSubmit      NEW · A4-1 · async (code) → serverResponse|throws.  When
 *                  provided, the component SKIPS its internal `api.post(scanUrl, …)`
 *                  and delegates the entire HTTP call to the parent.  The
 *                  returned value is then treated exactly like the server
 *                  response — passed to `onScanResult` (if any) or fed into
 *                  the operator-style progress logic.  Used by BARCODE/WMS
 *                  A4-1 Inbound Scan modal where a single "scan" fans out to
 *                  lookup + adjust API calls.  When absent, HTTP behaviour is
 *                  byte-identical to pre-A4-1.
 */
export default function WarehouseScanInput({
  sid, progress, scanned, onScanned, onReset, disabled,
  scanUrl,      // A3 · optional endpoint override (default = operator scan-match)
  onScanResult, // A3 · optional raw-response bubble (seller mode)
  onSubmit,     // A4-1 · optional full HTTP override (inbound scan mode)
}) {
  const inputRef = useRef(null);
  const [code, setCode] = useState('');
  const [feedback, setFeedback] = useState({ tone: 'idle', msg: '' });
  const [camSupported, setCamSupported] = useState(null);
  const [camOn, setCamOn] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const loopRef = useRef(null);

  // Autofocus for USB/BT keyboard-wedge scanners.
  useEffect(() => {
    if (!disabled && inputRef.current) inputRef.current.focus();
  }, [disabled, feedback]);

  useEffect(() => {
    // Detect BarcodeDetector once. No permission requested until user
    // presses [카메라 스캔].
    setCamSupported(typeof window !== 'undefined' &&
                     'BarcodeDetector' in window);
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopCamera = () => {
    try {
      if (loopRef.current) cancelAnimationFrame(loopRef.current);
      if (streamRef.current)
        streamRef.current.getTracks().forEach((t) => t.stop());
    } catch {}
    streamRef.current = null;
    setCamOn(false);
  };

  const startCamera = async () => {
    if (!camSupported) {
      toast.error('카메라를 사용할 수 없습니다. 직접 입력해주세요.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } }, audio: false,
      });
      streamRef.current = stream;
      const v = videoRef.current;
      if (v) { v.srcObject = stream; await v.play(); }
      // eslint-disable-next-line no-undef
      detectorRef.current = new window.BarcodeDetector();
      setCamOn(true);
      const tick = async () => {
        if (!streamRef.current) return;
        try {
          const codes = await detectorRef.current.detect(v);
          if (codes && codes[0]?.rawValue) {
            submit(codes[0].rawValue);
          }
        } catch {}
        loopRef.current = requestAnimationFrame(tick);
      };
      loopRef.current = requestAnimationFrame(tick);
    } catch (e) {
      toast.error('카메라 권한이 없습니다. 스캐너 또는 직접 입력을 이용해주세요.');
    }
  };

  const submit = async (raw) => {
    const c = String(raw || code || '').trim();
    if (!c) { setCode(''); return; }
    // A3 · endpoint override; default preserves operator READ-ONLY scan-match.
    const url = scanUrl || `/operator/orders/${sid}/scan-match`;
    try {
      // A4-1 · If parent supplies onSubmit, delegate the entire HTTP call.
      // Used by Inbound Scan modal which fans one scan out to lookup+adjust.
      const r = (typeof onSubmit === 'function')
        ? { data: await onSubmit(c) }
        : await api.post(url, { code: c });
      const m = r.data || {};
      // A3 · If parent supplies its own result handler, delegate fully and
      // SKIP internal operator-style progress tracking. The parent owns the
      // authoritative fulfillment state (e.g. seller ScanModal uses the
      // server-persisted `fulfillment` object returned by
      // /api/seller/fulfillment/{sid}/scan).
      if (typeof onScanResult === 'function') {
        onScanResult(m);
        setCode('');
        return;
      }
      if (!m.matched) {
        setFeedback({ tone: 'error', msg: m.detail || '매칭 실패' });
        setCode('');
        return;
      }
      const key = `${m.product_id}::${m.variant_key}`;
      const entry = (progress || []).find((p) =>
        `${p.product_id}::${p.variant_key}` === key);
      if (!entry) {
        setFeedback({ tone: 'error', msg: '이 주문에 없는 상품입니다.' });
      } else if (((scanned || {})[key] || 0) >= entry.required_qty) {
        setFeedback({ tone: 'error', msg: '주문수량을 초과했습니다.' });
      } else {
        onScanned && onScanned(m.product_id, m.variant_key, m.matched_by);
        setFeedback({ tone: 'ok',
          msg: `${entry.product_name || m.product_name} ${
            (((scanned || {})[key]) || 0) + 1}/${entry.required_qty} (${m.matched_by})`
        });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || '스캔 실패';
      // A3 · Bubble errors to seller-mode parent (e.g. 400 wrong-item /
      // 400 overscan / 400 no-match / 400 not-in-order); backend is the
      // single source of truth for these judgments — do NOT re-implement
      // them here.
      if (typeof onScanResult === 'function') {
        onScanResult({ error: detail });
        setCode('');
        return;
      }
      setFeedback({ tone: 'error', msg: detail });
    }
    setCode('');
  };

  const onKey = (e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } };

  return (
    <div className="border border-black/10 rounded-md p-3 bg-white"
          data-testid="warehouse-scan-panel">
      <div className="flex flex-wrap items-center gap-2">
        <input ref={inputRef}
                type="text" value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={onKey}
                placeholder="바코드 · SKU 스캔 (USB/BT 스캐너 · 키보드)"
                disabled={disabled}
                className="flex-1 min-w-[220px] border rounded px-3 py-2 text-sm"
                data-testid="warehouse-scan-input" />
        <button onClick={() => submit()} disabled={disabled}
                 className="px-3 py-2 text-sm bg-neutral-800 text-white rounded"
                 data-testid="warehouse-scan-submit">스캔</button>
        <button onClick={camOn ? stopCamera : startCamera}
                 className="px-3 py-2 text-sm border rounded"
                 data-testid="warehouse-camera-scan">
          {camOn ? '카메라 중지' : '카메라 스캔'}
        </button>
        <button onClick={() => { onReset && onReset(); setFeedback({ tone:'idle', msg:'' }); }}
                 className="px-3 py-2 text-sm border rounded"
                 data-testid="scan-reset">스캔 초기화</button>
      </div>
      {camOn && (
        <video ref={videoRef} className="mt-2 w-full max-w-sm rounded"
                muted playsInline />
      )}
      {camSupported === false && (
        <div className="mt-2 text-xs text-neutral-500"
              data-testid="warehouse-camera-unsupported">
          카메라 스캔 미지원 브라우저입니다. USB/BT 스캐너 또는 직접 입력을 이용해주세요.
        </div>
      )}
      {feedback.msg && (
        <div data-testid="scan-feedback"
              className={`mt-2 text-sm ${
                feedback.tone === 'ok' ? 'text-emerald-600'
                  : feedback.tone === 'error' ? 'text-red-600'
                  : 'text-neutral-600'}`}>
          {feedback.msg}
        </div>
      )}
    </div>
  );
}
